import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import IconClock from '@experimental-components/IconsComponents/Clock';
import ButtonPlus from '@experimental-components/ButtonPlus';
import IconClose from '@experimental-components/IconsComponents/Close';
import IconButton from '@design-system/IconButton';

import InputAdornmentFiled from '@design-system/InputAdornmentFiled';
import Checkbox from '@design-system/Checkbox';
import Select from '@design-system/Select';

import {
  formDefinition,
  getWeekDaysSelected,
  getWeekendDaysSelected,
  validateUppercase,
  getProvidersValues,
} from 'utils/menuMaker';
import { optionsTimes } from 'utils/times';
import { regexTypes, formatTextAllowedValues } from 'utils/formatField';

import CheckBoxChanel from 'components/MenuForm/CheckBoxChanel';
import useStyles from 'components/MenuForm/styles';
import { FeedbackBanner } from '@experimental-components/FeedbackBanner';
import { ButtonPrimary } from '@design-system/ButtonPrimary';

export function MenuForm({ onSaveMenu, disabled }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { control, errors, register, setValue, watch, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'timeFields' });
  const watchFields = watch();
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [selectedWeekendDays, setSelectedWeekendDays] = useState([]);

  useEffect(() => {
    setSelectedWeekDays(getWeekDaysSelected(fields));
    setSelectedWeekendDays(getWeekendDaysSelected(fields));
  }, [fields]);

  const handleRemoveSchedule = (index) => {
    const selectedWeekDaysTmp = selectedWeekDays.filter((item, indexSelected) => indexSelected !== index);
    const selectedWeekendDaysTmp = selectedWeekendDays.filter((item, indexSelected) => indexSelected !== index);

    setSelectedWeekDays(selectedWeekDaysTmp);
    setSelectedWeekendDays(selectedWeekendDaysTmp);
    remove(index);
  };

  const handleSelectAllWeekDays = (isChecked, index) => {
    const selectedWeekDaysTmp = JSON.parse(JSON.stringify(selectedWeekDays));
    selectedWeekDaysTmp[index] = [];

    formDefinition.weekdaysListCheckBox.forEach((weekDay) => {
      if (isChecked) selectedWeekDaysTmp[index].push(weekDay?.value);

      setValue(`timeFields[${index}].${weekDay?.name}`, isChecked ? weekDay?.value : false, { shouldValidate: true });
    });

    setSelectedWeekDays(selectedWeekDaysTmp);
  };

  const hanldeVerifyCheckedCheckBox = (array, value) => {
    return array?.indexOf(value) > -1;
  };

  const handleSelectWeekDay = (event, index) => {
    const selectedWeekDaysTmp = JSON.parse(JSON.stringify(selectedWeekDays));
    const selectedWeekDaysTmpIndex = JSON.parse(JSON.stringify(selectedWeekDaysTmp[index])).filter(
      (day) => day !== event.target.value,
    );

    if (event.target.checked) selectedWeekDaysTmpIndex.push(event.target.value);

    selectedWeekDaysTmp[index] = selectedWeekDaysTmpIndex;
    setSelectedWeekDays(selectedWeekDaysTmp);
  };

  const handleSelectAllWeekend = (isChecked, index) => {
    const selectedWeekendDaysTmp = JSON.parse(JSON.stringify(selectedWeekendDays));
    selectedWeekendDaysTmp[index] = [];

    formDefinition.weekendListCheckBox.forEach((weekendDay) => {
      if (isChecked) selectedWeekendDaysTmp[index].push(weekendDay?.value);

      setValue(`timeFields[${index}].${weekendDay?.name}`, isChecked ? weekendDay?.value : false, {
        shouldValidate: true,
      });
    });

    setSelectedWeekendDays(selectedWeekendDaysTmp);
  };

  const handleSelectWeekendDay = (event, index) => {
    const selectedWeekendDaysTmp = JSON.parse(JSON.stringify(selectedWeekendDays));
    const selectedWeekendDaysTmpIndex = JSON.parse(JSON.stringify(selectedWeekendDaysTmp[index])).filter(
      (day) => day !== event.target.value,
    );

    if (event.target.checked) selectedWeekendDaysTmpIndex.push(event.target.value);

    selectedWeekendDaysTmp[index] = selectedWeekendDaysTmpIndex;
    setSelectedWeekendDays(selectedWeekendDaysTmp);
  };

  const appendFormTimer = () => {
    setSelectedWeekDays([...selectedWeekDays, []]);
    setSelectedWeekendDays([...selectedWeekendDays, []]);
    append({});
  };

  const handleValidateAllowedValues = (target, field) => {
    target.value = formatTextAllowedValues(target.value, regexTypes.REGEX_PRODUCT_NAME_ALLOWED);
    setValue(field, target.value);
    return target.value;
  };

  return (
    <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Grid item>
        <InputAdornmentFiled
          {...formDefinition.nameMenuField}
          autoComplete="off"
          autoFocus
          data-testid="inputNameMenu"
          endAdornment={
            <InputAdornment position="end">
              <Box color="#CECDD1" fontSize="14px">
                {watch('nameMenuField')?.length ?? 0}/30
              </Box>
            </InputAdornment>
          }
          error={Boolean(errors[formDefinition.nameMenuField.name])}
          errorMsg={t(errors[formDefinition.nameMenuField.name]?.message, { chars: 30 })}
          fullWidth
          inputProps={{ maxLength: formDefinition.nameMenuField.maxLength }}
          inputRef={register({
            ...formDefinition.nameMenuField.register,
            validate: {
              controlUppercase: (value) => validateUppercase(value) || t('common:errors.controlUppercase'),
            },
          })}
          label={t(formDefinition.nameMenuField.label)}
          onChange={({ target }) => handleValidateAllowedValues(target, 'inputNameMenu')}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography className={classes.subtitles}>{t('menuMaker:createMenu.schedules.title')}</Typography>

        <Box mb={3}>
          <Typography color="textSecondary" variant="body2">
            {t('menuMaker:createMenu.schedules.header')}
          </Typography>
        </Box>

        <Grid container direction="column">
          {fields.map((field, index) => (
            <Grid item key={field.id}>
              <Box className={classes.paperHeader}>
                {index !== 0 && (
                  <IconButton data-testid="btnRemoveSchedule" onClick={() => handleRemoveSchedule(index)} size="large">
                    <IconClose lighter size={20} />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Controller
                    as={
                      <Select
                        {...formDefinition.startDateField}
                        data-testid="selectStartDate"
                        fullWidth
                        icon={<IconClock />}
                        label={t(formDefinition.startDateField.label)}
                      >
                        {optionsTimes.map((time) => (
                          <MenuItem key={time.value} value={time.value}>
                            {time.label}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                    control={control}
                    defaultValue={field?.startDateField || ''}
                    error={Boolean(errors?.timeFields?.length && errors?.timeFields[index]?.startDateField)}
                    helperText={t(errors?.timeFields?.length && errors?.timeFields[index]?.startDateField?.message)}
                    name={`timeFields[${index}].${formDefinition.startDateField.name}`}
                    rules={{ ...formDefinition.startDateField.rules }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    as={
                      <Select
                        {...formDefinition.endDateField}
                        data-testid="selectEndDate"
                        fullWidth
                        icon={<IconClock />}
                        label={t(formDefinition.endDateField.label)}
                        onClick={() => trigger()}
                      >
                        {optionsTimes.map((time) => (
                          <MenuItem key={time.value} value={time.value}>
                            {time.label}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                    control={control}
                    defaultValue={field?.endDateField || ''}
                    error={Boolean(errors?.timeFields?.length && errors?.timeFields[index]?.endDateField)}
                    helperText={t(errors?.timeFields?.length && errors?.timeFields[index]?.endDateField?.message)}
                    name={`timeFields[${index}].${formDefinition.endDateField.name}`}
                    rules={{ ...formDefinition.endDateField.rules }}
                  />
                </Grid>
              </Grid>

              <Box mt={2}>
                <Grid className={classes.weekdaysContainer} container>
                  <Grid item>
                    <Box>
                      <Checkbox
                        checked={selectedWeekDays[index]?.length === 5}
                        className={classes.checkBoxSelectAll}
                        data-testid="weekDaysCheck"
                        indeterminate={selectedWeekDays[index]?.length === 5}
                        label={t('menuMaker:createMenu.fields.weekday')}
                        onChange={({ target: { checked } }) => handleSelectAllWeekDays(checked, index)}
                      />
                    </Box>

                    <FormControl component="fieldset" sx={{ maxWidth: 300 }} variant="standard">
                      <FormGroup row>
                        {formDefinition.weekdaysListCheckBox.map((weekdaysCheck) => (
                          <Checkbox
                            {...weekdaysCheck}
                            checked={hanldeVerifyCheckedCheckBox(selectedWeekDays[index], weekdaysCheck.value)}
                            className={classes.weekdays}
                            data-testid={weekdaysCheck.name}
                            inputRef={register()}
                            key={weekdaysCheck.name}
                            label={t(weekdaysCheck.label)}
                            name={`timeFields[${index}].${weekdaysCheck.name}`}
                            onChange={(e) => handleSelectWeekDay(e, index)}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </Grid>

                  <Grid item>
                    <Box>
                      <Checkbox
                        checked={selectedWeekendDays[index]?.length === 2}
                        className={classes.checkBoxSelectAll}
                        data-testid="weekendDaysCheck"
                        indeterminate={selectedWeekendDays[index]?.length === 2}
                        label={t('menuMaker:createMenu.fields.weekend')}
                        onChange={(e) => handleSelectAllWeekend(e.target.checked, index)}
                      />
                    </Box>

                    <FormControl component="fieldset" variant="standard">
                      <FormGroup row>
                        {formDefinition.weekendListCheckBox.map((weekendCheck) => (
                          <Checkbox
                            {...weekendCheck}
                            checked={hanldeVerifyCheckedCheckBox(selectedWeekendDays[index], weekendCheck.value)}
                            className={classes.weekdays}
                            data-testid={weekendCheck.name}
                            inputRef={register()}
                            key={weekendCheck.name}
                            label={t(weekendCheck.label)}
                            name={`timeFields[${index}].${weekendCheck.name}`}
                            onChange={(e) => handleSelectWeekendDay(e, index)}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              {index + 1 !== fields.length ? (
                <Box my={4}>
                  <Divider />
                </Box>
              ) : null}
            </Grid>
          ))}
        </Grid>

        <Box mt={3}>
          <ButtonPlus data-testid="btnAddSchedule" onClick={appendFormTimer}>
            {t('menuMaker:createMenu.schedules.buttonAdd')}
          </ButtonPlus>
        </Box>

        <Box mt={4}>
          <Divider />
        </Box>
      </Grid>

      <Grid item>
        <Typography className={classes.subtitles}>{t('menuMaker:createMenu.chanels.title')}</Typography>

        <Box>
          <Typography color="textSecondary" variant="body2">
            {t('menuMaker:createMenu.chanels.header')}
          </Typography>
        </Box>

        <Box mt={3}>
          <FormControl component="fieldset" fullWidth variant="standard">
            <FormGroup className={classes.formChanel}>
              {formDefinition.providersOptions.map((providerOption) => (
                <CheckBoxChanel
                  data-testid={`provider-option-${providerOption.id}`}
                  defaultChecked={Boolean(watchFields[providerOption.name])}
                  inputRef={register({ ...providerOption.register })}
                  key={providerOption.name}
                  label={t(providerOption.label)}
                  name={providerOption.name}
                  value={providerOption.value}
                />
              ))}
            </FormGroup>
          </FormControl>
          {getProvidersValues(watchFields)?.length === 0 && (
            <Box className={classes.errorMessagesCustom} mt={0}>
              {t('menuMaker:createMenu.fieldErrors.selectProviders')}
            </Box>
          )}
        </Box>
      </Grid>
      <FeedbackBanner description={<Trans i18nKey="menuMaker:importMenu.saveMenu.bannerInfo" />} type="info" />
      <Box display="flex" justifyContent="flex-start">
        <ButtonPrimary disabled={disabled} onClick={onSaveMenu}>
          {t('menuMaker:importMenu.saveMenu.buttonAccept')}
        </ButtonPrimary>
      </Box>
    </Grid>
  );
}
